/**
 * Shift-held wall labels (Synth VR), in small white text. A wall with a side is captioned Left or Right
 * inside the wall, as high as it fits and as near the wall's visual centre as it can be (`labelSpot`);
 * squares, triangles, crouches and the centre wall have no side caption. Every wall's roll in degrees
 * sits where its move gizmo is centred, in the corner between the X and Y arrows (`rollLabelSpot`).
 * Mounted only while Shift is down or walls are being dragged (an Alt-drag, where Q and E roll them, or a
 * move handle), so it costs nothing the rest of the time.
 */
import React, { useMemo, useState } from 'react';
import { Text, useFBX } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import { useEditorStore, OBSTACLE_SHAPE_FBX, DEFAULT_OBSTACLE_CONFIGS } from '../../store/editorStore';
import type { ObstacleConfigKey } from '../../store/editorStore';
import type { ObstacleShape } from '../../store/editorSettings';
import { isSynthVrView } from '../../store/editorSettings';
import { PERF } from '../../perf/adaptiveBudget';
import { BEAT_SPACING, chartBeat, VISIBLE_BEATS_BEHIND, useShiftHeld, isGizmoDragActive } from './TunnelScene';
import { isObjectDragActive } from './objectDrag';
import { getObstacleMergedGeometry, wallDrawXY, CONFIGURABLE_KEYS } from './ObstacleModel';
import { wallXY, desktopWallDefaults, crouchWallEntries } from './wallGeometry';
import { sceneFontFor } from './sceneFont';
import { convexHull, drawnOutline, labelSpot, rollLabelSpot, degrees, wallSideText, type Pt } from './wallLabelSpot';

const NOTE_Y_OFFSET = 1.8;            // the height walls are drawn above the floor (ObstacleModel)
const FONT_SIZE = 0.1;
const MAX_LABELS = 80;                // a dense section would otherwise mount hundreds of text meshes
const SHAPES: ObstacleShape[] = ['regular', 'diagonal', 'center', 'square', 'triangle'];

interface Item {
  key: string; beat: number; shape: ObstacleShape;
  cx: number; cy: number;             // where the wall is drawn
  sx: number; sy: number;             // its drawn scale, negative on X for a left wall
  roll: number;                       // the roll it is drawn with, degrees
  side: string;                       // 'Left', 'Right' or '' (no side caption)
  rollText: string;
}

const textProps = {
  fontSize: FONT_SIZE, color: '#FFFFFF', outlineWidth: FONT_SIZE * 0.06, outlineColor: '#000000',
  outlineOpacity: 0.6, renderOrder: 30,
} as const;


function LabelText({ item, hull, font }: { item: Item; hull: Pt[] | undefined; font: string | undefined }) {
  const [x, y] = useMemo(() => {
    if (!hull || !item.side) return [0, 0] as Pt;
    const halfW = (item.side.length * FONT_SIZE * 0.56) / 2, halfH = FONT_SIZE * 0.42;
    return labelSpot(drawnOutline(hull, item.sx, item.sy, item.roll), halfW, halfH);
    // The spot depends on the wall's outline and the text's size, not on where the wall is: a drag
    // rebuilds every item each frame and must not redo the search for walls that did not change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hull, item.sx, item.sy, item.roll, item.side.length]);
  const z = -item.beat * BEAT_SPACING + 0.06;
  const [rx, ry] = rollLabelSpot(item.cx, item.cy);
  return (
    <>
      {item.side && (
        <Text position={[item.cx + x, item.cy + y, z]} font={font} anchorX="center" anchorY="middle" {...textProps}>
          {item.side}
        </Text>
      )}
      <Text position={[rx, ry, z]} font={font} anchorX="left" anchorY="bottom" {...textProps}>
        {item.rollText}
      </Text>
    </>
  );
}

function Labels() {
  const fbx = {
    regular: useFBX(OBSTACLE_SHAPE_FBX.regular.fbx),
    diagonal: useFBX(OBSTACLE_SHAPE_FBX.diagonal.fbx),
    center: useFBX(OBSTACLE_SHAPE_FBX.center.fbx),
    square: useFBX(OBSTACLE_SHAPE_FBX.square.fbx),
    triangle: useFBX(OBSTACLE_SHAPE_FBX.triangle.fbx),
  };
  const hulls = useMemo(() => {
    const out: Partial<Record<ObstacleShape, Pt[]>> = {};
    for (const shape of SHAPES) {
      const pos = getObstacleMergedGeometry(shape, fbx[shape])?.getAttribute('position');
      if (!pos) continue;
      const pts: Pt[] = [];
      for (let i = 0; i < pos.count; i++) pts.push([pos.getX(i), pos.getY(i)]);
      out[shape] = convexHull(pts);
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fbx.regular, fbx.diagonal, fbx.center, fbx.square, fbx.triangle]);

  const diff = useEditorStore(s => s.beatmap?.difficulties[s.activeDifficulty]);
  const bpm = useEditorStore(s => s.beatmap?.bpm || 120);
  const gp = useEditorStore(s => s.gameplaySettings);
  const font = useEditorStore(s => sceneFontFor(s.activeView));

  // The window moves a beat at a time: re-pick the labelled walls only when the playhead crosses one.
  const [beat, setBeat] = useState(() => Math.floor(chartBeat()));
  useFrame(() => { const b = Math.floor(chartBeat()); if (b !== beat) setBeat(b); });

  const items = useMemo(() => {
    if (!diff) return [];
    const lo = beat - Math.min(VISIBLE_BEATS_BEHIND, PERF.obstacleViewBeats), hi = beat + 1 + PERF.obstacleViewBeats;
    const bs = gp.obstacleScale;
    const out: Item[] = [];
    (diff.slides ?? []).forEach((s, i) => {
      if (s.time_beats < lo || s.time_beats > hi || !CONFIGURABLE_KEYS.has(s.wall_type)) return;
      const cfg = gp.obstacleConfigs[s.wall_type as ObstacleConfigKey] ?? DEFAULT_OBSTACLE_CONFIGS[s.wall_type as ObstacleConfigKey] ?? DEFAULT_OBSTACLE_CONFIGS.center;
      const [wx, wy] = wallDrawXY(s.position, cfg);
      const left = s.wall_type === 'wall_left' || s.wall_type === 'angle_left';
      out.push({
        key: `s${i}`, beat: s.time_beats, shape: cfg.shape, cx: wx, cy: NOTE_Y_OFFSET + wy,
        sx: (left ? -bs : bs) * (cfg.scaleX ?? 1), sy: bs * (cfg.scaleY ?? 1),
        roll: cfg.rotation + (s.z_rotation ?? 0), side: wallSideText(s.wall_type), rollText: degrees(s.z_rotation ?? 0),
      });
    });
    const solids: Array<[ObstacleShape, typeof diff.squares]> = [['square', diff.squares], ['triangle', diff.triangles]];
    for (const [shape, list] of solids) {
      (list ?? []).forEach((o, i) => {
        const t = o.time_beats ?? 0;
        if (t < lo || t > hi) return;
        const d = desktopWallDefaults(shape as 'square' | 'triangle');
        const [wx, wy] = wallXY(o.position, d.defaultX, d.defaultY);
        out.push({
          key: `${shape}${i}`, beat: t, shape, cx: wx, cy: NOTE_Y_OFFSET + wy,
          sx: bs * d.scaleX, sy: bs * d.scaleY, roll: o.z_rotation ?? 0, side: '', rollText: degrees(o.z_rotation ?? 0),
        });
      });
    }
    const crouch = gp.obstacleConfigs.crouch ?? DEFAULT_OBSTACLE_CONFIGS.crouch;
    for (const c of crouchWallEntries(diff.crouches ?? [], diff.crouch_obstacles)) {
      const t = (c.timeMs / 60000) * bpm;
      if (t < lo || t > hi) continue;
      const [wx, wy] = wallDrawXY(c.position, crouch);
      out.push({
        key: `c${c.timeMs}`, beat: t, shape: crouch.shape, cx: wx, cy: NOTE_Y_OFFSET + wy,
        sx: bs * (crouch.scaleX ?? 1), sy: bs * (crouch.scaleY ?? 1), roll: crouch.rotation, side: '', rollText: degrees(0),   // a crouch carries no roll of its own
      });
    }
    return out.sort((a, b) => Math.abs(a.beat - beat) - Math.abs(b.beat - beat)).slice(0, MAX_LABELS);
  }, [diff, bpm, gp.obstacleScale, gp.obstacleConfigs, beat]);

  return <>{items.map(it => <LabelText key={it.key} item={it} hull={hulls[it.shape]} font={font} />)}</>;
}

/** True while the user drags walls, by an Alt-drag or a move handle. Read each frame: the drags keep
 *  their state outside React, and this only re-renders when it flips. */
function useWallDragging(): boolean {
  const [dragging, setDragging] = useState(false);
  useFrame(() => {
    const now = isObjectDragActive() || isGizmoDragActive();
    if (now !== dragging) setDragging(now);
  });
  return dragging;
}

export default function WallLabels() {
  const vr = useEditorStore(s => isSynthVrView(s.activeView));
  const shift = useShiftHeld();
  const dragging = useWallDragging();
  if (!vr || !(shift || dragging)) return null;
  return <React.Suspense fallback={null}><Labels /></React.Suspense>;
}
